import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ContentTypeFilterKey } from '../../../ts/models/ContentFilters';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { contentTypeFilterSelected, filterContentType } from '../../actions/spaces/ContentExplorerFilterActions';

import { useIsMounted, usePrevious } from '../../hooks';
import { selectDefaultContentTypeFilterValues } from '../../selectors/ContentFilterSelectors';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { getContentTypesFromQueryParam } from '../Explorer/ExplorerUrlBindings';

export interface ContentTypeFilterUpdaterOwnProps {
  readonly routeLocationSearch: string;
  readonly routeLocationPath: string;
  readonly resetStore?: boolean;
}

export interface ContentTypeFilterUpdaterStateProps {
  readonly defaultContentTypeValues: ReadonlyArray<ExplorableItemType>;
}

export interface ContentTypeFilterUpdaterDispatchProps {
  readonly filterContentType: (contentTypes: ReadonlyArray<ExplorableItemType>) => void;
  readonly contentTypeFilterSelected: (contentTypes: ReadonlyArray<ExplorableItemType>, clearAndSet: boolean) => void;
}

export type ContentTypeFilterUpdaterProps = ContentTypeFilterUpdaterOwnProps
  & ContentTypeFilterUpdaterStateProps
  & ContentTypeFilterUpdaterDispatchProps;

/**
 * Component that syncs the content types from the URL query params to the Redux store before rendering its children.
 */
export const ContentTypeFilterUpdater: React.FunctionComponent<ContentTypeFilterUpdaterProps> = props => {
  const isMounted = useIsMounted();
  const prevRouteLocationPath = usePrevious(props.routeLocationPath);
  const queryParams = queryString.parse(props.routeLocationSearch);
  const contentTypes = getContentTypesFromQueryParam(queryParams[ContentTypeFilterKey], props.defaultContentTypeValues);

  // Before the first render, initialize the content type filter in the store
  // TFSID:983648.  Handle case when updating this component with a new projectId
  if (!isMounted || props.resetStore) {
    props.filterContentType(contentTypes);
  }

  // Whenever the content type query param changes, update the content type filter in the store
  React.useEffect(() => {
    if (isMounted && prevRouteLocationPath === props.routeLocationPath) {
      props.contentTypeFilterSelected(contentTypes, null);
    }
  }, [ queryParams[ContentTypeFilterKey] ]);

  return <>{props.children}</>;
};

const mapStateToProps: MapStateToProps<ContentTypeFilterUpdaterStateProps> = state => ({
  defaultContentTypeValues: selectDefaultContentTypeFilterValues(state),
});

const mapDispatchToProps: MapDispatchToProps<ContentTypeFilterUpdaterDispatchProps> = dispatch => bindActionCreators({
  filterContentType,
  contentTypeFilterSelected,
}, dispatch);

export const ConnectedContentTypeFilterUpdater = connect(mapStateToProps, mapDispatchToProps)(ContentTypeFilterUpdater);

/**
 * Creates a higher order component that wraps the given component with the ContentTypeFilterUpdater component.
 */
export function withContentTypeFilterUpdater<P>(Component: React.ComponentType<P>) {
  return (props: P & ContentTypeFilterUpdaterOwnProps) => (
    <ConnectedContentTypeFilterUpdater {...props}><Component {...props}/></ConnectedContentTypeFilterUpdater>
  );
}
